import type { SheetSize } from '@forgd/ui-kit/sheet'
import type { Component } from 'vue'
import DtAdjustDistribution from '#design-tokenomics/components/adjust/DtAdjustDistribution.vue'
import DtAdjustEmission from '#design-tokenomics/components/adjust/DtAdjustEmission.vue'
import DtAdjustFdv from '#design-tokenomics/components/adjust/DtAdjustFdv.vue'
import DtAdjustPostTgePop from '#design-tokenomics/components/adjust/DtAdjustPostTgePop.vue'
import DtAdjustSellPressure from '#design-tokenomics/components/adjust/DtAdjustSellPressure.vue'
import { useAppSlideover } from '#core/composables/useAppSlideover'

export type QuickAdjustSlug =
  | 'fdv'
  | 'postTgePop'
  | 'sellPressure'
  | 'distribution'
  | 'emission'
  | 'estimatingDemand'

interface QuickAdjustPanel {
  title: string
  component: Component
  size?: SheetSize
  flush?: boolean
}

const panels: Partial<Record<QuickAdjustSlug, QuickAdjustPanel>> = {
  fdv: {
    title: 'FDV & Max. Token Supply',
    component: DtAdjustFdv,
  },
  postTgePop: {
    title: 'Simulating a Post-TGE "Pop"',
    component: DtAdjustPostTgePop,
    size: 'sm',
  },
  sellPressure: {
    title: 'Modeling Sell Pressure: Cost Basis and Potential Sources of Sell Pressure',
    component: DtAdjustSellPressure,
    flush: true,
  },
  distribution: {
    title: 'Token Distribution Schedule',
    component: DtAdjustDistribution,
  },
  emission: {
    title: 'Token Emission Schedule',
    component: DtAdjustEmission,
  },
}

export function useDesignQuickAdjust() {
  const { model: isOpen } = useAppSlideover('design-tokenomics-quick-adjust')
  const current = useState<QuickAdjustSlug | null>('design-tokenomics:quick-adjust', () => null)
  const route = useRoute()

  watch(() => route.path, () => {
    if (isOpen.value) {
      close()
    }
  })

  const panel = computed(() => (current.value ? panels[current.value] ?? null : null))

  const size = computed<SheetSize>(() => panel.value?.size ?? 'lg')

  function hasPanel(slug: QuickAdjustSlug) {
    return slug in panels
  }

  function open(slug: QuickAdjustSlug) {
    if (!hasPanel(slug)) {
      return
    }

    current.value = slug
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
    current.value = null
  }

  return {
    isOpen,
    panel,
    size,
    hasPanel,
    open,
    close,
  }
}
